import { sql } from "drizzle-orm";
import { index, sqliteTable, text, integer, uniqueIndex } from "drizzle-orm/sqlite-core";
import * as authSchema from "./auth.schema";
import * as oauthSchema from "./oauth.schema";
import { generateULID } from "./snowflake";
import type { DbSchemaSnapshot, DbSchemaTable } from "./db-schema-snapshot";

const ts = (name: string) => integer(name, { mode: "timestamp" });

// Define column schema type locally to avoid circular dependencies
export type ColumnSchema = {
  name: string;
  prompt: string;
  primaryKey?: boolean;
  unique?: boolean;
  autoIncrement?: boolean;
  nullable?: boolean;
  reanalyzeExisting?: boolean;
};

export const jobStatusEnum = [
  "queued",
  "in_progress",
  "completed",
  "failed",
  "waiting",
] as const;
export const documentStatusEnum = [
  "queued",
  "classifying",
  "classified",
  "unclassified",
  "processing",
  "completed",
  "failed",
  "error",
] as const;
export const subscriptionStatusEnum = [
  "active",
  "inactive",
  "cancelled",
  "past_due",
] as const;
export const subscriptionPlanEnum = [
  "free",
  "basic",
  "pro",
  "enterprise",
] as const;
export const extractionStatusEnum = ["success", "failed"] as const;
export const workflowTypeEnum = ["ocr", "reanalyze"] as const;

export const emailInboxStatusEnum = ["active", "paused", "disabled"] as const;
export type EmailInboxStatus = (typeof emailInboxStatusEnum)[number];

export type EmailSenderAllowlistRule =
  | { type: "address"; value: string }
  | { type: "domain"; value: string };

export const inboundEmailMessageStatusEnum = [
  "stored",
  "outbox_pending",
  "queued",
  "processing",
  "retrying",
  "processed",
  "failed",
  "dead_lettered",
] as const;
export type InboundEmailMessageStatus =
  (typeof inboundEmailMessageStatusEnum)[number];

export const inboundEmailAttachmentStatusEnum = [
  "stored",
  "queued",
  "processing",
  "dispatched",
  "processed",
  "unsupported",
  "failed",
] as const;
export type InboundEmailAttachmentStatus =
  (typeof inboundEmailAttachmentStatusEnum)[number];

export const emailIngestionReservationStatusEnum = [
  "pending",
  "reserved",
  "settled",
  "released",
] as const;

export const emailIngestionOutboxStatusEnum = [
  "pending",
  "published",
] as const;

export const processingRunStatusEnum = [
  "queued",
  "running",
  "waiting_for_review",
  "cancelling",
  "succeeded",
  "partially_succeeded",
  "failed",
  "cancelled",
] as const;
export type ProcessingRunStatus = (typeof processingRunStatusEnum)[number];

export const processingWorkItemStatusEnum = [
  "queued",
  "running",
  "waiting_for_review",
  "succeeded",
  "failed",
  "cancelled",
] as const;
export type ProcessingWorkItemStatus =
  (typeof processingWorkItemStatusEnum)[number];

export const processingReviewStatusEnum = [
  "open",
  "claimed",
  "resolved",
  "expired",
  "cancelled",
] as const;
export type ProcessingReviewStatus =
  (typeof processingReviewStatusEnum)[number];

export const processingOutboxStatusEnum = [
  "pending",
  "publishing",
  "published",
  "failed",
] as const;
export type ProcessingOutboxStatus =
  (typeof processingOutboxStatusEnum)[number];

export const splitterStatusEnum = ["draft", "published", "archived"] as const;
export type SplitterStatus = (typeof splitterStatusEnum)[number];

export const splitRunStatusEnum = [
  "queued",
  "running",
  "waiting_for_review",
  "succeeded",
  "partially_succeeded",
  "failed",
  "cancelled",
] as const;
export type SplitRunStatus = (typeof splitRunStatusEnum)[number];

export const segmentReviewStatusEnum = [
  "not_required",
  "pending",
  "approved",
  "rejected",
] as const;
export type SegmentReviewStatus = (typeof segmentReviewStatusEnum)[number];

export const projects = sqliteTable("projects", {
  id: integer("id", { mode: "number" }).primaryKey({ autoIncrement: true }),
  name: text("name").notNull(),
  cloudflareId: text("cloudflare_id").notNull(),
  slug: text("slug").notNull().default("lame-slug"),
  user_id: text("user_id")
    .references(() => authSchema.users.id, { onDelete: "cascade" })
    .notNull(),
  database: text("database").notNull(),
  fileSize: integer("file_size"),
  instantModeThreshold: integer("instant_mode_threshold")
    .default(20)
    .notNull(),
  passwordProtectionEnabled: integer("password_protection_enabled", { mode: "boolean" })
    .default(false)
    .notNull(),
  createdAt: ts("created_at").$defaultFn(() => new Date()),
  updatedAt: ts("updated_at").$onUpdateFn(() => new Date()),
}, (table) => ({
    projects_user_id_fk_idx: index("projects_user_id_fk_idx").on(table.user_id),
  }));

export const mcp_workflow_setups = sqliteTable(
  "mcp_workflow_setups",
  {
    id: text("id").primaryKey(),
    user_id: text("user_id").notNull().references(() => authSchema.users.id, { onDelete: "cascade" }),
    intent_hash: text("intent_hash").notNull(),
    status: text("status", { enum: ["pending", "running", "partial", "completed"] }).notNull().default("pending"),
    state: text("state", { mode: "json" }).$type<Record<string, unknown>>().notNull().default({}),
    lease_expires_at: ts("lease_expires_at"),
    error_message: text("error_message"),
    created_at: ts("created_at").notNull().$defaultFn(() => new Date()),
    updated_at: ts("updated_at").notNull().$defaultFn(() => new Date()).$onUpdateFn(() => new Date()),
  },
  (table) => ({ ownerIdx: index("mcp_workflow_setups_user_id_idx").on(table.user_id) }),
);

export const project_api_keys = sqliteTable(
  "project_api_keys",
  {
    id: text("id").primaryKey().$defaultFn(() => generateULID()),
    project_id: integer("project_id").notNull().references(() => projects.id, { onDelete: "cascade" }),
    user_id: text("user_id").notNull().references(() => authSchema.users.id, { onDelete: "cascade" }),
    name: text("name"),
    key_hash: text("key_hash").notNull(),
    key_prefix: text("key_prefix").notNull(),
    permissions: text("permissions", { mode: "json" }).$type<Record<string, string[]>>().notNull(),
    expires_at: ts("expires_at"),
    revoked_at: ts("revoked_at"),
    revoked_by: text("revoked_by").references(() => authSchema.users.id, { onDelete: "set null" }),
    revocation_reason: text("revocation_reason"),
    rotated_from_id: text("rotated_from_id"),
    rotation_grace_ends_at: ts("rotation_grace_ends_at"),
    last_used_at: ts("last_used_at"),
    created_at: ts("created_at").notNull().$defaultFn(() => new Date()),
    updated_at: ts("updated_at").notNull().$defaultFn(() => new Date()).$onUpdateFn(() => new Date()),
  },
  (table) => ({
    keyHashUnique: uniqueIndex("project_api_keys_key_hash_unique").on(table.key_hash),
    projectIdx: index("project_api_keys_project_id_idx").on(table.project_id),
    ownerIdx: index("project_api_keys_user_id_idx").on(table.user_id),
    project_api_keys_revoked_by_fk_idx: index("project_api_keys_revoked_by_fk_idx").on(table.revoked_by).where(sql`${table.revoked_by} IS NOT NULL`),
  }),
);

export const password_vault = sqliteTable("password_vault", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => generateULID()),
  name: text("name"),
  projectId: integer("projects_id")
    .references(() => projects.id, { onDelete: "cascade" })
    .notNull(),
  userId: text("user_id")
    .references(() => authSchema.users.id)
    .notNull(),
  encrypted_password: text("encrypted_password").notNull(),
  encryption_nonce: text("encryption_nonce").notNull(),
  disabled: integer("disabled", { mode: "boolean" }).default(false).notNull(),
  created_at: ts("created_at")
    .$defaultFn(() => new Date())
    .notNull(),
  updated_at: ts("updated_at")
    .$defaultFn(() => new Date())
    .notNull(),
  last_used_at: ts("last_used_at"),
}, (table) => ({
    password_vault_projects_id_fk_idx: index("password_vault_projects_id_fk_idx").on(table.projectId),
    password_vault_user_id_fk_idx: index("password_vault_user_id_fk_idx").on(table.userId),
  }));

export const tables = sqliteTable("tables", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => generateULID()),
  name: text("name").notNull(),
  slug: text("slug").notNull().default("lame-slug"),
  columns: text("columns", { mode: "json" }).$type<ColumnSchema[]>(),
  active_schema_revision_id: text("active_schema_revision_id"),
  latest_schema_revision: integer("latest_schema_revision").notNull().default(0),
  batchId: text("batch_id"),
  user_id: text("user_id")
    .references(() => authSchema.users.id, { onDelete: "cascade" })
    .notNull(),
  lastExractedAt: ts("last_extracted_at"),
  createdAt: ts("inserted_at")
    .$defaultFn(() => new Date())
    .notNull(),
  updatedAt: ts("updated_at").$onUpdateFn(() => new Date()),
  projects_id: integer("projects_id")
    .references(() => projects.id, { onDelete: "cascade" })
    .notNull(),
}, (table) => ({
    tables_user_id_fk_idx: index("tables_user_id_fk_idx").on(table.user_id),
    tables_projects_id_fk_idx: index("tables_projects_id_fk_idx").on(table.projects_id),
  }));

export const project_email_inboxes = sqliteTable(
  "project_email_inboxes",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    project_id: integer("project_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    user_id: text("user_id")
      .references(() => authSchema.users.id, { onDelete: "cascade" })
      .notNull(),
    table_id: text("table_id").references(() => tables.id, {
      onDelete: "set null",
    }),
    name: text("name").notNull(),
    address: text("address").notNull(),
    cloudflare_rule_id: text("cloudflare_rule_id"),
    status: text("status", { enum: emailInboxStatusEnum })
      .notNull()
      .default("active"),
    sender_allowlist: text("sender_allowlist", { mode: "json" })
      .$type<EmailSenderAllowlistRule[]>()
      .notNull()
      .default([]),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: ts("updated_at")
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    addressUnique: uniqueIndex("project_email_inboxes_address_unique").on(
      table.address,
    ),
    cloudflareRuleUnique: uniqueIndex(
      "project_email_inboxes_cloudflare_rule_id_unique",
    ).on(table.cloudflare_rule_id),
    projectIdx: index("project_email_inboxes_project_id_idx").on(
      table.project_id,
    ),
    ownerIdx: index("project_email_inboxes_user_id_idx").on(table.user_id),
    tableIdx: index("project_email_inboxes_table_id_idx").on(table.table_id),
  }),
);

export const converter_workspaces = sqliteTable(
  "converter_workspaces",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    user_id: text("user_id")
      .references(() => authSchema.users.id, { onDelete: "cascade" })
      .notNull(),
    type: text("type").notNull().default("pdf_to_csv"),
    name: text("name").notNull().default("PDF to CSV"),
    project_id: integer("project_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    table_id: text("table_id")
      .references(() => tables.id, { onDelete: "cascade" })
      .notNull(),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: ts("updated_at")
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    userTypeIdx: index("converter_workspaces_user_type_idx").on(
      table.user_id,
      table.type,
    ),
    ownerIdx: index("converter_workspaces_user_id_idx").on(table.user_id),
    converter_workspaces_project_id_fk_idx: index("converter_workspaces_project_id_fk_idx").on(table.project_id),
    converter_workspaces_table_id_fk_idx: index("converter_workspaces_table_id_fk_idx").on(table.table_id),
  }),
);

export const conversions = sqliteTable(
  "conversions",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    workspace_id: text("workspace_id")
      .references(() => converter_workspaces.id, { onDelete: "cascade" })
      .notNull(),
    user_id: text("user_id")
      .references(() => authSchema.users.id, { onDelete: "cascade" })
      .notNull(),
    job_id: integer("job_id").references(() => jobs.id, {
      onDelete: "set null",
    }),
    name: text("name").notNull(),
    status: text("status").notNull().default("draft"),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: ts("updated_at")
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date())
      .notNull(),
    completedAt: ts("completed_at"),
  },
  (table) => ({
    workspaceIdx: index("conversions_workspace_id_idx").on(table.workspace_id),
    ownerIdx: index("conversions_user_id_idx").on(table.user_id),
    jobUnique: uniqueIndex("conversions_job_id_unique").on(table.job_id),
  }),
);

export const conversion_documents = sqliteTable(
  "conversion_documents",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    conversion_id: text("conversion_id")
      .references(() => conversions.id, { onDelete: "cascade" })
      .notNull(),
    document_id: integer("document_id")
      .references(() => documents.id, { onDelete: "cascade" })
      .notNull(),
    position: integer("position").notNull().default(0),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    conversionDocumentUnique: uniqueIndex(
      "conversion_documents_conversion_document_unique",
    ).on(table.conversion_id, table.document_id),
    conversionIdx: index("conversion_documents_conversion_id_idx").on(
      table.conversion_id,
    ),
    conversion_documents_document_id_fk_idx: index("conversion_documents_document_id_fk_idx").on(table.document_id),
  }),
);

export const classifiers = sqliteTable("classifiers", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => generateULID()),
  name: text("name").notNull(),
  prompt: text("prompt").notNull(),
  confidenceThreshold: integer("confidence_threshold").default(70).notNull(),
  project_id: integer("project_id")
    .references(() => projects.id, { onDelete: "cascade" })
    .notNull(),
  table_id: text("table_id")
    .references(() => tables.id, { onDelete: "cascade" })
    .notNull(),
  user_id: text("user_id")
    .references(() => authSchema.users.id, { onDelete: "cascade" })
    .notNull(),
  enabled: integer("enabled", { mode: "boolean" }).default(true).notNull(),
  createdAt: ts("created_at")
    .$defaultFn(() => new Date())
    .notNull(),
  updatedAt: ts("updated_at").$onUpdateFn(() => new Date()),
}, (table) => ({
    classifiers_project_id_fk_idx: index("classifiers_project_id_fk_idx").on(table.project_id),
    classifiers_table_id_fk_idx: index("classifiers_table_id_fk_idx").on(table.table_id),
    classifiers_user_id_fk_idx: index("classifiers_user_id_fk_idx").on(table.user_id),
  }));

export const batch_processes = sqliteTable("batch_processes", {
  id: integer("id", { mode: "number" }).primaryKey({ autoIncrement: true }),
  outputFile: text("output_file"),
  batchId: text("batch_id").notNull().unique(),
  workflowInstanceId: text("workflow_instance_id").notNull().unique(),
  completedRequests: integer("completed_requests"),
  job_id: integer("job_id").references(() => jobs.id, {
    onDelete: "cascade",
  }),
  failedRequests: integer("failed_requests"),
  errorId: text("error_id"),
  user_id: text("user_id")
    .references(() => authSchema.users.id, { onDelete: "cascade" })
    .notNull(),
  status: text("status").notNull().default("init"),
  reconcileRestartAttempts: integer("reconcile_restart_attempts")
    .default(0)
    .notNull(),
  lastReconcileAt: ts("last_reconcile_at"),
  reconcileLastNote: text("reconcile_last_note"),
  project_id: integer("projects_id")
    .references(() => projects.id, { onDelete: "cascade" })
    .notNull(),
  createdAt: ts("created_at").$defaultFn(() => new Date()),
  updatedAt: ts("updated_at").$onUpdateFn(() => new Date()),
}, (table) => ({
  jobStatusIdx: index("batch_processes_job_id_status_idx").on(table.job_id, table.status),
  statusIdx: index("batch_processes_status_updated_at_idx").on(table.status, table.updatedAt),

    batch_processes_user_id_fk_idx: index("batch_processes_user_id_fk_idx").on(table.user_id),
    batch_processes_projects_id_fk_idx: index("batch_processes_projects_id_fk_idx").on(table.project_id),
  }));

export const jobs = sqliteTable(
  "jobs",
  {
    id: integer("id", { mode: "number" }).primaryKey({ autoIncrement: true }),
    user_id: text("user_id")
      .references(() => authSchema.users.id, { onDelete: "cascade" })
      .notNull(),
    projects_id: integer("projects_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    workflow_type: text("workflowType").default("insert-workflow"),
    idempotency_key: text("idempotency_key"),
    work_item_count: integer("work_item_count").notNull().default(1),
    table_id: text("table_id")
      .references(() => tables.id, { onDelete: "cascade" })
      .notNull(),
    createdAt: ts("inserted_at")
      .notNull()
      .$defaultFn(() => new Date()),
    updatedAt: ts("updated_at")
      .notNull()
      .$onUpdateFn(() => new Date())
      .$defaultFn(() => new Date()),
    jobStatus: text("job_status").default("queued").notNull(),
  },
  (table) => ({
    idempotencyKeyUnique: uniqueIndex("jobs_idempotency_key_unique").on(
      table.idempotency_key,
    ),
    statusUpdatedIdx: index("jobs_status_updated_at_idx").on(table.jobStatus, table.updatedAt),
    ownerProjectIdx: index("jobs_user_id_projects_id_idx").on(table.user_id, table.projects_id),
    ownerTableCreatedIdx: index("jobs_user_table_created_idx").on(table.user_id, table.table_id, table.createdAt),
    jobs_projects_id_fk_idx: index("jobs_projects_id_fk_idx").on(table.projects_id),
    jobs_table_id_fk_idx: index("jobs_table_id_fk_idx").on(table.table_id),
  }),
);

export const documents = sqliteTable(
  "documents",
  {
    id: integer("id", { mode: "number" }).primaryKey({ autoIncrement: true }),
    file_id: text("file_id"),
    upload_id: text("upload_id"),
    ingestion_source: text("ingestion_source"),
    ingestion_source_id: text("ingestion_source_id"),
    is_duplicate: integer("is_duplicate", { mode: "boolean" }).default(false),
    password_protection_check_enabled: integer("password_protection", { mode: "boolean" }).default(
      false,
    ),
    password_protection_status: text("password_protection_status").default(
      "unencrypted",
    ),
    duplicate_count: integer("duplicate_count").default(0),
    mistral_id: text("mistral_id"),
    name: text("name"),
    user_id: text("user_id").references(() => authSchema.users.id, {
      onDelete: "set null",
    }),
    processedAt: ts("last_completed_job_date"),
    status: text("document_status").default("queued").notNull(),
    file_size: integer("file_size"),
    page_count: integer("page_count"),
    orphaned_at: ts("orphaned_at"),
    createdAt: ts("inserted_at")
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: ts("updated_at").$onUpdateFn(() => new Date()),
    project_id: integer("project_id").references(() => projects.id, {
      onDelete: "set null",
    }),
    table_id: text("table_id").references(() => tables.id, {
      onDelete: "set null",
    }),
  },
  (table) => ({
    ingestionSourceIdUnique: uniqueIndex(
      "documents_ingestion_source_id_unique",
    ).on(table.ingestion_source_id),
    fileIdIdx: index("documents_file_id_idx").on(table.file_id),
    ownerProjectIdx: index("documents_user_id_project_id_inserted_at_idx").on(
      table.user_id,
      table.project_id,
      table.createdAt,
    ),
    tableStatusIdx: index("documents_table_id_status_idx").on(table.table_id, table.status),
    documents_project_id_fk_idx: index("documents_project_id_fk_idx").on(table.project_id).where(sql`${table.project_id} IS NOT NULL`),
  }),
);

export const inbound_email_messages = sqliteTable(
  "inbound_email_messages",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    inbox_id: text("inbox_id")
      .references(() => project_email_inboxes.id, { onDelete: "cascade" })
      .notNull(),
    project_id: integer("project_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    user_id: text("user_id")
      .references(() => authSchema.users.id, { onDelete: "cascade" })
      .notNull(),
    idempotency_key: text("idempotency_key").notNull(),
    rfc_message_id: text("rfc_message_id"),
    raw_sha256: text("raw_sha256").notNull(),
    envelope_from: text("envelope_from").notNull(),
    recipient_address: text("recipient_address").notNull(),
    subject: text("subject"),
    route_kind: text("route_kind").notNull().default("classify"),
    route_table_id: text("route_table_id"),
    route_table_name: text("route_table_name"),
    route_database_id: text("route_database_id"),
    attachment_count: integer("attachment_count").notNull().default(0),
    accepted_attachment_count: integer("accepted_attachment_count")
      .notNull()
      .default(0),
    status: text("status", { enum: inboundEmailMessageStatusEnum })
      .notNull()
      .default("stored"),
    status_reason: text("status_reason"),
    processing_attempts: integer("processing_attempts").notNull().default(0),
    processingStartedAt: ts("processing_started_at"),
    leaseExpiresAt: ts("lease_expires_at"),
    cleanupCompletedAt: ts("cleanup_completed_at"),
    receivedAt: ts("received_at")
      .$defaultFn(() => new Date())
      .notNull(),
    processedAt: ts("processed_at"),
    updatedAt: ts("updated_at")
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    idempotencyKeyUnique: uniqueIndex(
      "inbound_email_messages_idempotency_key_unique",
    ).on(table.idempotency_key),
    inboxReceivedIdx: index("inbound_email_messages_inbox_received_idx").on(
      table.inbox_id,
      table.receivedAt,
    ),
    projectStatusIdx: index(
      "inbound_email_messages_project_status_idx",
    ).on(table.project_id, table.status),
    inbound_email_messages_user_id_fk_idx: index("inbound_email_messages_user_id_fk_idx").on(table.user_id),
  }),
);

export const inbound_email_attachments = sqliteTable(
  "inbound_email_attachments",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    message_id: text("message_id")
      .references(() => inbound_email_messages.id, { onDelete: "cascade" })
      .notNull(),
    document_id: integer("document_id").references(() => documents.id, {
      onDelete: "set null",
    }),
    reservation_id: text("reservation_id"),
    r2DeletedAt: ts("r2_deleted_at"),
    sequence: integer("sequence").notNull(),
    filename: text("filename").notNull(),
    content_type: text("content_type").notNull(),
    disposition: text("disposition"),
    content_id: text("content_id"),
    size: integer("size").notNull(),
    page_count: integer("page_count"),
    sha256: text("sha256").notNull(),
    r2_key: text("r2_key"),
    status: text("status", { enum: inboundEmailAttachmentStatusEnum })
      .notNull()
      .default("stored"),
    status_reason: text("status_reason"),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: ts("updated_at")
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    messageSequenceUnique: uniqueIndex(
      "inbound_email_attachments_message_sequence_unique",
    ).on(table.message_id, table.sequence),
    r2KeyUnique: uniqueIndex("inbound_email_attachments_r2_key_unique").on(
      table.r2_key,
    ),
    documentUnique: uniqueIndex(
      "inbound_email_attachments_document_id_unique",
    ).on(table.document_id),
    reservationUnique: uniqueIndex(
      "inbound_email_attachments_reservation_id_unique",
    ).on(table.reservation_id),
    messageStatusIdx: index(
      "inbound_email_attachments_message_status_idx",
    ).on(table.message_id, table.status),
  }),
);

export const email_ingestion_reservations = sqliteTable(
  "email_ingestion_reservations",
  {
    id: text("id").primaryKey(),
    message_id: text("message_id")
      .references(() => inbound_email_messages.id, { onDelete: "cascade" })
      .notNull(),
    attachment_id: text("attachment_id").notNull(),
    subscription_id: integer("subscription_id")
      .references(() => subscriptions.id, { onDelete: "cascade" })
      .notNull(),
    user_id: text("user_id")
      .references(() => authSchema.users.id, { onDelete: "cascade" })
      .notNull(),
    tokens: integer("tokens").notNull(),
    storage_bytes: integer("storage_bytes").notNull(),
    r2_key: text("r2_key").notNull(),
    status: text("status", { enum: emailIngestionReservationStatusEnum })
      .notNull()
      .default("reserved"),
    createdAt: ts("created_at").notNull(),
    settledAt: ts("settled_at"),
    releasedAt: ts("released_at"),
  },
  (table) => ({
    attachmentUnique: uniqueIndex(
      "email_ingestion_reservations_attachment_id_unique",
    ).on(table.attachment_id),
    messageStatusIdx: index(
      "email_ingestion_reservations_message_status_idx",
    ).on(table.message_id, table.status),
    email_ingestion_reservations_subscription_id_fk_idx: index("email_ingestion_reservations_subscription_id_fk_idx").on(table.subscription_id),
    email_ingestion_reservations_user_id_fk_idx: index("email_ingestion_reservations_user_id_fk_idx").on(table.user_id),
  }),
);

export const email_ingestion_rate_buckets = sqliteTable(
  "email_ingestion_rate_buckets",
  {
    id: text("id").primaryKey(),
    inbox_id: text("inbox_id")
      .references(() => project_email_inboxes.id, { onDelete: "cascade" })
      .notNull(),
    user_id: text("user_id")
      .references(() => authSchema.users.id, { onDelete: "cascade" })
      .notNull(),
    window_started_at: ts("window_started_at").notNull(),
    receipt_count: integer("receipt_count").notNull().default(0),
    updatedAt: ts("updated_at").notNull(),
  },
  (table) => ({
    inboxWindowIdx: index("email_ingestion_rate_buckets_inbox_window_idx").on(
      table.inbox_id,
      table.window_started_at,
    ),
    email_ingestion_rate_buckets_user_id_fk_idx: index("email_ingestion_rate_buckets_user_id_fk_idx").on(table.user_id),
  }),
);

export const email_ingestion_outbox = sqliteTable(
  "email_ingestion_outbox",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    message_id: text("message_id")
      .references(() => inbound_email_messages.id, { onDelete: "cascade" })
      .notNull(),
    status: text("status", { enum: emailIngestionOutboxStatusEnum })
      .notNull()
      .default("pending"),
    publish_attempts: integer("publish_attempts").notNull().default(0),
    last_error: text("last_error"),
    createdAt: ts("created_at").notNull(),
    publishedAt: ts("published_at"),
    updatedAt: ts("updated_at").notNull(),
  },
  (table) => ({
    messageUnique: uniqueIndex("email_ingestion_outbox_message_id_unique").on(
      table.message_id,
    ),
    statusIdx: index("email_ingestion_outbox_status_idx").on(table.status),
  }),
);

export const document_extraction_attempts = sqliteTable(
  "document_extraction_attempts",
  {
    id: integer("id", { mode: "number" }).primaryKey({ autoIncrement: true }),
    document_id: integer("document_id")
      .references(() => documents.id, { onDelete: "cascade" })
      .notNull(),
    segment_id: text("segment_id"),
    job_id: integer("job_id").references(() => jobs.id, {
      onDelete: "cascade",
    }),
    correlation_id: text("correlation_id"),
    workflow_instance_id: text("workflow_instance_id").notNull(),
    workflow_type: text("workflow_type").notNull(),
    status: text("status").notNull(),
    extracted_data: text("extracted_data", { mode: "json" }),
    error_message: text("error_message"),
    error_details: text("error_details"),
    attempt_number: integer("attempt_number").notNull(),
    started_at: ts("started_at").notNull(),
    completed_at: ts("completed_at").notNull(),
    processing_duration_ms: integer("processing_duration_ms"),
    user_id: text("user_id")
      .references(() => authSchema.users.id, { onDelete: "cascade" })
      .notNull(),
    project_id: integer("project_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    table_id: text("table_id")
      .references(() => tables.id, { onDelete: "cascade" })
      .notNull(),
    createdAt: ts("created_at").$defaultFn(() => new Date()),
    updatedAt: ts("updated_at").$onUpdateFn(() => new Date()),
  },
  (table) => ({
    jobAttemptIdx: index("extraction_attempts_job_attempt_idx").on(table.job_id, table.attempt_number, table.id),
    documentSegmentAttemptIdx: index("extraction_attempts_document_segment_attempt_idx").on(table.document_id, table.segment_id, table.attempt_number),
    document_extraction_attempts_user_id_fk_idx: index("document_extraction_attempts_user_id_fk_idx").on(table.user_id),
    document_extraction_attempts_project_id_fk_idx: index("document_extraction_attempts_project_id_fk_idx").on(table.project_id),
    document_extraction_attempts_table_id_fk_idx: index("document_extraction_attempts_table_id_fk_idx").on(table.table_id),
  }),
);

export const document_classifications = sqliteTable("document_classifications", {
  id: integer("id", { mode: "number" }).primaryKey({ autoIncrement: true }),
  document_id: integer("document_id")
    .references(() => documents.id, { onDelete: "cascade" })
    .notNull(),
  workflow_instance_id: text("workflow_instance_id"),
  matched_classifier_id: text("matched_classifier_id").references(
    () => classifiers.id,
    { onDelete: "set null" },
  ),
  confidence_score: integer("confidence_score"),
  classification_reasoning: text("classification_reasoning"),
  all_scores: text("all_scores", { mode: "json" }).$type<
    Array<{
      classifierId: string;
      classifierName: string;
      score: number;
      reasoning: string;
    }>
  >(),
  status: text("status").notNull(),
  error_message: text("error_message"),
  processing_duration_ms: integer("processing_duration_ms"),
  user_id: text("user_id")
    .references(() => authSchema.users.id, { onDelete: "cascade" })
    .notNull(),
  project_id: integer("project_id")
    .references(() => projects.id, { onDelete: "cascade" })
    .notNull(),
  createdAt: ts("created_at").$defaultFn(() => new Date()),
}, (table) => ({
    document_classifications_document_id_fk_idx: index("document_classifications_document_id_fk_idx").on(table.document_id),
    document_classifications_matched_classifier_id_fk_idx: index("document_classifications_matched_classifier_id_fk_idx").on(table.matched_classifier_id).where(sql`${table.matched_classifier_id} IS NOT NULL`),
    document_classifications_user_id_fk_idx: index("document_classifications_user_id_fk_idx").on(table.user_id),
    document_classifications_project_id_fk_idx: index("document_classifications_project_id_fk_idx").on(table.project_id),
  }));

export const splitters = sqliteTable(
  "splitters",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    project_id: integer("project_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    user_id: text("user_id")
      .references(() => authSchema.users.id, { onDelete: "cascade" })
      .notNull(),
    source_inbox_id: text("source_inbox_id").references(
      () => project_email_inboxes.id,
      { onDelete: "set null" },
    ),
    name: text("name").notNull(),
    description: text("description"),
    mode: text("mode").notNull().default("page_range"),
    status: text("status", { enum: splitterStatusEnum })
      .notNull()
      .default("draft"),
    draft_config: text("draft_config", { mode: "json" })
      .$type<Record<string, unknown>>()
      .notNull(),
    draft_revision: integer("draft_revision").notNull().default(1),
    latest_version: integer("latest_version").notNull().default(0),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: ts("updated_at")
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    projectNameUnique: uniqueIndex("splitters_project_name_unique").on(
      table.project_id,
      table.name,
    ),
    projectStatusIdx: index("splitters_project_status_idx").on(
      table.project_id,
      table.status,
    ),
    splitters_user_id_fk_idx: index("splitters_user_id_fk_idx").on(table.user_id),
    splitters_source_inbox_id_fk_idx: index("splitters_source_inbox_id_fk_idx").on(table.source_inbox_id).where(sql`${table.source_inbox_id} IS NOT NULL`),
  }),
);

export const splitter_versions = sqliteTable(
  "splitter_versions",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    splitter_id: text("splitter_id")
      .references(() => splitters.id, { onDelete: "cascade" })
      .notNull(),
    version: integer("version").notNull(),
    mode: text("mode").notNull(),
    revision_hash: text("revision_hash").notNull(),
    config: text("config", { mode: "json" })
      .$type<Record<string, unknown>>()
      .notNull(),
    published_by: text("published_by").references(() => authSchema.users.id, {
      onDelete: "set null",
    }),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    splitterVersionUnique: uniqueIndex(
      "splitter_versions_splitter_version_unique",
    ).on(table.splitter_id, table.version),
    splitterHashUnique: uniqueIndex(
      "splitter_versions_splitter_hash_unique",
    ).on(table.splitter_id, table.revision_hash),
    splitter_versions_published_by_fk_idx: index("splitter_versions_published_by_fk_idx").on(table.published_by).where(sql`${table.published_by} IS NOT NULL`),
  }),
);

export const splitter_sections = sqliteTable(
  "splitter_sections",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    splitter_version_id: text("splitter_version_id")
      .references(() => splitter_versions.id, { onDelete: "cascade" })
      .notNull(),
    section_key: text("section_key").notNull(),
    name: text("name").notNull(),
    description: text("description"),
    destination_table_id: text("destination_table_id").references(
      () => tables.id,
      { onDelete: "restrict" },
    ),
    page_spans: text("page_spans", { mode: "json" })
      .$type<Array<{ start: number; end: number }>>()
      .notNull(),
    ordinal: integer("ordinal").notNull(),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    versionKeyUnique: uniqueIndex("splitter_sections_version_key_unique").on(
      table.splitter_version_id,
      table.section_key,
    ),
    versionOrdinalUnique: uniqueIndex(
      "splitter_sections_version_ordinal_unique",
    ).on(table.splitter_version_id, table.ordinal),
    splitter_sections_destination_table_id_fk_idx: index("splitter_sections_destination_table_id_fk_idx").on(table.destination_table_id).where(sql`${table.destination_table_id} IS NOT NULL`),
  }),
);

export const processing_config_snapshots = sqliteTable(
  "processing_config_snapshots",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    project_id: integer("project_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    pipeline_definition_id: text("pipeline_definition_id")
      .notNull()
      .default("document-processing"),
    pipeline_version: integer("pipeline_version").notNull().default(1),
    checksum: text("checksum").notNull(),
    snapshot: text("snapshot", { mode: "json" })
      .$type<Record<string, unknown>>()
      .notNull(),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    projectChecksumUnique: uniqueIndex(
      "processing_config_snapshots_project_checksum_unique",
    ).on(table.project_id, table.checksum),
    projectVersionIdx: index(
      "processing_config_snapshots_project_version_idx",
    ).on(table.project_id, table.pipeline_version),
  }),
);

export const processing_runs = sqliteTable(
  "processing_runs",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    document_id: integer("document_id")
      .references(() => documents.id, { onDelete: "cascade" })
      .notNull(),
    job_id: integer("job_id").references(() => jobs.id, {
      onDelete: "set null",
    }),
    user_id: text("user_id")
      .references(() => authSchema.users.id, { onDelete: "cascade" })
      .notNull(),
    project_id: integer("project_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    config_snapshot_id: text("config_snapshot_id")
      .references(() => processing_config_snapshots.id, {
        onDelete: "restrict",
      })
      .notNull(),
    pipeline_definition_id: text("pipeline_definition_id")
      .notNull()
      .default("document-processing"),
    pipeline_version: integer("pipeline_version").notNull().default(1),
    trigger: text("trigger").notNull(),
    priority: integer("priority").notNull().default(0),
    status: text("status", { enum: processingRunStatusEnum })
      .notNull()
      .default("queued"),
    current_stage: text("current_stage").notNull().default("admit"),
    workflow_instance_id: text("workflow_instance_id"),
    idempotency_key: text("idempotency_key").notNull(),
    usage_reservation_id: text("usage_reservation_id"),
    failure_code: text("failure_code"),
    failure_message: text("failure_message"),
    startedAt: ts("started_at"),
    completedAt: ts("completed_at"),
    lastHeartbeatAt: ts("last_heartbeat_at"),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: ts("updated_at")
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    idempotencyKeyUnique: uniqueIndex(
      "processing_runs_idempotency_key_unique",
    ).on(table.idempotency_key),
    projectStatusIdx: index("processing_runs_project_status_idx").on(
      table.project_id,
      table.status,
    ),
    documentCreatedIdx: index("processing_runs_document_created_idx").on(
      table.document_id,
      table.createdAt,
    ),
    processing_runs_job_id_fk_idx: index("processing_runs_job_id_fk_idx").on(table.job_id).where(sql`${table.job_id} IS NOT NULL`),
    processing_runs_user_id_fk_idx: index("processing_runs_user_id_fk_idx").on(table.user_id),
    processing_runs_config_snapshot_id_fk_idx: index("processing_runs_config_snapshot_id_fk_idx").on(table.config_snapshot_id),
  }),
);

export const split_runs = sqliteTable(
  "split_runs",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    processing_run_id: text("processing_run_id")
      .references(() => processing_runs.id, { onDelete: "cascade" })
      .notNull(),
    splitter_version_id: text("splitter_version_id")
      .references(() => splitter_versions.id, { onDelete: "restrict" })
      .notNull(),
    status: text("status", { enum: splitRunStatusEnum })
      .notNull()
      .default("queued"),
    segment_count: integer("segment_count").notNull().default(0),
    review_count: integer("review_count").notNull().default(0),
    failure_code: text("failure_code"),
    failure_message: text("failure_message"),
    startedAt: ts("started_at"),
    completedAt: ts("completed_at"),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: ts("updated_at")
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    runVersionUnique: uniqueIndex("split_runs_run_version_unique").on(
      table.processing_run_id,
      table.splitter_version_id,
    ),
    versionStatusIdx: index("split_runs_version_status_idx").on(
      table.splitter_version_id,
      table.status,
    ),
  }),
);

export const document_segments = sqliteTable(
  "document_segments",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    split_run_id: text("split_run_id")
      .references(() => split_runs.id, { onDelete: "cascade" })
      .notNull(),
    processing_run_id: text("processing_run_id")
      .references(() => processing_runs.id, { onDelete: "cascade" })
      .notNull(),
    document_id: integer("document_id")
      .references(() => documents.id, { onDelete: "cascade" })
      .notNull(),
    splitter_section_id: text("splitter_section_id")
      .references(() => splitter_sections.id, { onDelete: "restrict" }),
    section_key: text("section_key"),
    section_name: text("section_name"),
    segment_key: text("segment_key").notNull(),
    ordinal: integer("ordinal").notNull(),
    page_spans: text("page_spans", { mode: "json" })
      .$type<Array<{ start: number; end: number }>>()
      .notNull(),
    page_count: integer("page_count").notNull(),
    confidence: integer("confidence"),
    partition_key: text("partition_key"),
    evidence: text("evidence", { mode: "json" }).$type<
      Array<Record<string, unknown>>
    >(),
    review_status: text("review_status", { enum: segmentReviewStatusEnum })
      .notNull()
      .default("not_required"),
    destination_table_id: text("destination_table_id").references(
      () => tables.id,
      { onDelete: "restrict" },
    ),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: ts("updated_at")
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    runSegmentKeyUnique: uniqueIndex(
      "document_segments_run_segment_key_unique",
    ).on(table.split_run_id, table.segment_key),
    runOrdinalUnique: uniqueIndex("document_segments_run_ordinal_unique").on(
      table.split_run_id,
      table.ordinal,
    ),
    documentIdx: index("document_segments_document_idx").on(
      table.document_id,
      table.createdAt,
    ),
    document_segments_processing_run_id_fk_idx: index("document_segments_processing_run_id_fk_idx").on(table.processing_run_id),
    document_segments_splitter_section_id_fk_idx: index("document_segments_splitter_section_id_fk_idx").on(table.splitter_section_id).where(sql`${table.splitter_section_id} IS NOT NULL`),
    document_segments_destination_table_id_fk_idx: index("document_segments_destination_table_id_fk_idx").on(table.destination_table_id).where(sql`${table.destination_table_id} IS NOT NULL`),
  }),
);

export const processing_artifacts = sqliteTable(
  "processing_artifacts",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    run_id: text("run_id")
      .references(() => processing_runs.id, { onDelete: "cascade" })
      .notNull(),
    source_artifact_id: text("source_artifact_id"),
    kind: text("kind").notNull(),
    storage_key: text("storage_key").notNull(),
    checksum: text("checksum").notNull(),
    byte_size: integer("byte_size").notNull(),
    media_type: text("media_type").notNull(),
    page_start: integer("page_start"),
    page_end: integer("page_end"),
    creating_stage: text("creating_stage").notNull(),
    stage_version: integer("stage_version").notNull(),
    retention_class: text("retention_class").notNull().default("run"),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    runKindChecksumUnique: uniqueIndex(
      "processing_artifacts_run_kind_checksum_unique",
    ).on(table.run_id, table.kind, table.checksum),
    storageKeyUnique: uniqueIndex(
      "processing_artifacts_storage_key_unique",
    ).on(table.storage_key),
  }),
);

export const processing_work_items = sqliteTable(
  "processing_work_items",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    run_id: text("run_id")
      .references(() => processing_runs.id, { onDelete: "cascade" })
      .notNull(),
    segment_id: text("segment_id").notNull().default("document"),
    destination_id: text("destination_id").notNull(),
    table_id: text("table_id")
      .references(() => tables.id, { onDelete: "restrict" })
      .notNull(),
    schema_revision_id: text("schema_revision_id").notNull(),
    status: text("status", { enum: processingWorkItemStatusEnum })
      .notNull()
      .default("queued"),
    attempt_count: integer("attempt_count").notNull().default(0),
    input_artifact_id: text("input_artifact_id").references(
      () => processing_artifacts.id,
      { onDelete: "set null" },
    ),
    output_artifact_id: text("output_artifact_id").references(
      () => processing_artifacts.id,
      { onDelete: "set null" },
    ),
    result_hash: text("result_hash"),
    failure_code: text("failure_code"),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: ts("updated_at")
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    scopeUnique: uniqueIndex("processing_work_items_scope_unique").on(
      table.run_id,
      table.segment_id,
      table.destination_id,
      table.table_id,
      table.schema_revision_id,
    ),
    runStatusIdx: index("processing_work_items_run_status_idx").on(
      table.run_id,
      table.status,
    ),
    processing_work_items_table_id_fk_idx: index("processing_work_items_table_id_fk_idx").on(table.table_id),
    processing_work_items_input_artifact_id_fk_idx: index("processing_work_items_input_artifact_id_fk_idx").on(table.input_artifact_id).where(sql`${table.input_artifact_id} IS NOT NULL`),
    processing_work_items_output_artifact_id_fk_idx: index("processing_work_items_output_artifact_id_fk_idx").on(table.output_artifact_id).where(sql`${table.output_artifact_id} IS NOT NULL`),
  }),
);

export const processing_stage_receipts = sqliteTable(
  "processing_stage_receipts",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    run_id: text("run_id")
      .references(() => processing_runs.id, { onDelete: "cascade" })
      .notNull(),
    stage_name: text("stage_name").notNull(),
    stage_version: integer("stage_version").notNull(),
    scope_id: text("scope_id").notNull().default("document"),
    input_hash: text("input_hash").notNull(),
    output_artifact_id: text("output_artifact_id").references(
      () => processing_artifacts.id,
      { onDelete: "set null" },
    ),
    result_hash: text("result_hash"),
    status: text("status").notNull(),
    provider: text("provider"),
    model: text("model"),
    metrics: text("metrics", { mode: "json" }).$type<Record<string, unknown>>(),
    error_code: text("error_code"),
    error_message: text("error_message"),
    retry_class: text("retry_class"),
    attempt: integer("attempt").notNull().default(1),
    startedAt: ts("started_at").notNull(),
    completedAt: ts("completed_at").notNull(),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    receiptUnique: uniqueIndex("processing_stage_receipts_unique").on(
      table.run_id,
      table.stage_name,
      table.stage_version,
      table.scope_id,
      table.input_hash,
    ),
    runStageIdx: index("processing_stage_receipts_run_stage_idx").on(
      table.run_id,
      table.stage_name,
    ),
    processing_stage_receipts_output_artifact_id_fk_idx: index("processing_stage_receipts_output_artifact_id_fk_idx").on(table.output_artifact_id).where(sql`${table.output_artifact_id} IS NOT NULL`),
  }),
);

export const processing_review_tasks = sqliteTable(
  "processing_review_tasks",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    run_id: text("run_id")
      .references(() => processing_runs.id, { onDelete: "cascade" })
      .notNull(),
    document_id: integer("document_id")
      .references(() => documents.id, { onDelete: "cascade" })
      .notNull(),
    scope: text("scope").notNull(),
    scope_id: text("scope_id").notNull().default("document"),
    reason_code: text("reason_code").notNull(),
    candidates: text("candidates", { mode: "json" })
      .$type<Array<Record<string, unknown>>>()
      .notNull(),
    confidence: integer("confidence"),
    status: text("status", { enum: processingReviewStatusEnum })
      .notNull()
      .default("open"),
    assigned_to: text("assigned_to").references(() => authSchema.users.id, {
      onDelete: "set null",
    }),
    lease_expires_at: ts("lease_expires_at"),
    deadline_at: ts("deadline_at"),
    resolvedAt: ts("resolved_at"),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: ts("updated_at")
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    openScopeUnique: uniqueIndex("processing_review_tasks_scope_unique").on(
      table.run_id,
      table.scope,
      table.scope_id,
    ),
    runStatusIdx: index("processing_review_tasks_run_status_idx").on(
      table.run_id,
      table.status,
    ),
    processing_review_tasks_document_id_fk_idx: index("processing_review_tasks_document_id_fk_idx").on(table.document_id),
    processing_review_tasks_assigned_to_fk_idx: index("processing_review_tasks_assigned_to_fk_idx").on(table.assigned_to).where(sql`${table.assigned_to} IS NOT NULL`),
  }),
);

export const processing_review_decisions = sqliteTable(
  "processing_review_decisions",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    review_task_id: text("review_task_id")
      .references(() => processing_review_tasks.id, { onDelete: "cascade" })
      .notNull(),
    run_id: text("run_id")
      .references(() => processing_runs.id, { onDelete: "cascade" })
      .notNull(),
    actor_user_id: text("actor_user_id").references(() => authSchema.users.id, {
      onDelete: "set null",
    }),
    decision: text("decision", { mode: "json" })
      .$type<Record<string, unknown>>()
      .notNull(),
    note: text("note"),
    supersedes_decision_id: text("supersedes_decision_id"),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    taskCreatedIdx: index("processing_review_decisions_task_created_idx").on(
      table.review_task_id,
      table.createdAt,
    ),
    processing_review_decisions_run_id_fk_idx: index("processing_review_decisions_run_id_fk_idx").on(table.run_id),
    processing_review_decisions_actor_user_id_fk_idx: index("processing_review_decisions_actor_user_id_fk_idx").on(table.actor_user_id).where(sql`${table.actor_user_id} IS NOT NULL`),
  }),
);

export const processing_outbox = sqliteTable(
  "processing_outbox",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    run_id: text("run_id")
      .references(() => processing_runs.id, { onDelete: "cascade" })
      .notNull(),
    event_id: text("event_id").notNull(),
    event_type: text("event_type").notNull(),
    payload_version: integer("payload_version").notNull().default(1),
    destination: text("destination").notNull(),
    payload: text("payload", { mode: "json" })
      .$type<Record<string, unknown>>()
      .notNull(),
    status: text("status", { enum: processingOutboxStatusEnum })
      .notNull()
      .default("pending"),
    publish_attempts: integer("publish_attempts").notNull().default(0),
    last_error: text("last_error"),
    next_attempt_at: ts("next_attempt_at"),
    publishedAt: ts("published_at"),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: ts("updated_at")
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    eventIdUnique: uniqueIndex("processing_outbox_event_id_unique").on(
      table.event_id,
    ),
    statusAttemptIdx: index("processing_outbox_status_attempt_idx").on(
      table.status,
      table.next_attempt_at,
    ),
    processing_outbox_run_id_fk_idx: index("processing_outbox_run_id_fk_idx").on(table.run_id),
  }),
);

export const db_schema = sqliteTable("db_schema", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => generateULID()),
  projectId: integer("project_id")
    .references(() => projects.id, { onDelete: "cascade" })
    .notNull()
    .unique(),
  snapshot: text("snapshot", { mode: "json" }).$type<DbSchemaSnapshot>().notNull(),
  createdAt: ts("created_at").$defaultFn(() => new Date()),
  updatedAt: ts("updated_at").$onUpdateFn(() => new Date()),
});

export const table_schema_revisions = sqliteTable(
  "table_schema_revisions",
  {
    id: text("id").primaryKey().$defaultFn(() => generateULID()),
    project_id: integer("project_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    table_id: text("table_id")
      .references(() => tables.id, { onDelete: "cascade" })
      .notNull(),
    revision: integer("revision").notNull(),
    checksum: text("checksum").notNull(),
    schema: text("schema", { mode: "json" }).$type<DbSchemaTable>().notNull(),
    createdAt: ts("created_at").$defaultFn(() => new Date()).notNull(),
  },
  (table) => ({
    tableRevisionUnique: uniqueIndex(
      "table_schema_revisions_table_revision_unique",
    ).on(table.table_id, table.revision),
    tableChecksumUnique: uniqueIndex(
      "table_schema_revisions_table_checksum_unique",
    ).on(table.table_id, table.checksum),
    projectIdx: index("table_schema_revisions_project_idx").on(
      table.project_id,
      table.table_id,
    ),
  }),
);

export const table_relationships = sqliteTable(
  "table_relationships",
  {
    id: text("id").primaryKey().$defaultFn(() => generateULID()),
    project_id: integer("project_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    child_table_id: text("child_table_id")
      .references(() => tables.id, { onDelete: "cascade" })
      .notNull(),
    parent_table_id: text("parent_table_id")
      .references(() => tables.id, { onDelete: "cascade" })
      .notNull(),
    child_column: text("child_column").notNull(),
    parent_column: text("parent_column").notNull(),
    relationship_type: text("relationship_type").notNull().default("many_to_one"),
    active: integer("active", { mode: "boolean" }).notNull().default(true),
    createdAt: ts("created_at").$defaultFn(() => new Date()).notNull(),
    updatedAt: ts("updated_at").$defaultFn(() => new Date()).notNull(),
  },
  (table) => ({
    relationshipUnique: uniqueIndex("table_relationships_edge_unique").on(
      table.child_table_id,
      table.child_column,
      table.parent_table_id,
      table.parent_column,
    ),
    parentIdx: index("table_relationships_parent_idx").on(
      table.project_id,
      table.parent_table_id,
      table.active,
    ),
    childIdx: index("table_relationships_child_idx").on(
      table.project_id,
      table.child_table_id,
      table.active,
    ),
    table_relationships_parent_table_id_fk_idx: index("table_relationships_parent_table_id_fk_idx").on(table.parent_table_id),
  }),
);

// These tables contain existing customer extraction schemas. They remain part
// of the canonical D1 schema until every project has been safely consolidated.
export const table_metadata = sqliteTable("table_metadata", {
  id: text("id").primaryKey(),
  project_id: integer("project_id", { mode: "number" })
    .references(() => projects.id, { onDelete: "cascade" })
    .notNull(),
  table_id: text("table_id")
    .references(() => tables.id, { onDelete: "cascade" })
    .notNull(),
  name: text("name").notNull(),
  display_name: text("display_name"),
  description: text("description"),
  created_at: ts("created_at"),
  updated_at: ts("updated_at"),
}, (table) => ({
    table_metadata_project_id_fk_idx: index("table_metadata_project_id_fk_idx").on(table.project_id),
    table_metadata_table_id_fk_idx: index("table_metadata_table_id_fk_idx").on(table.table_id),
  }));

export const column_metadata = sqliteTable("column_metadata", {
  id: text("id").primaryKey(),
  table_metadata_id: text("table_metadata_id")
    .references(() => table_metadata.id, { onDelete: "cascade" })
    .notNull(),
  key: text("key").notNull(),
  prompt: text("prompt"),
  type: text("type").notNull(),
  order_index: integer("order_index").default(0),
  is_primary_key: integer("is_primary_key", { mode: "boolean" }).default(false),
  is_required: integer("is_required", { mode: "boolean" }).default(false),
  reanalyze_existing: integer("reanalyze_existing", { mode: "boolean" }).default(false),
  created_at: ts("created_at"),
  updated_at: ts("updated_at"),
  referenced_table: text("referenced_table"),
  referenced_column: text("referenced_column"),
  parent_table: text("parent_table"),
}, (table) => ({
    column_metadata_table_metadata_id_fk_idx: index("column_metadata_table_metadata_id_fk_idx").on(table.table_metadata_id),
  }));

export const agents = sqliteTable("agents", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => generateULID()),
  user_id: text("user_id")
    .references(() => authSchema.users.id, { onDelete: "cascade" })
    .notNull(),
  name: text("name").notNull(),
  description: text("description"),
  status: text("status").notNull().default("active"),
  createdAt: ts("created_at").$defaultFn(() => new Date()),
  updatedAt: ts("updated_at").$onUpdateFn(() => new Date()),
}, (table) => ({
    agents_user_id_fk_idx: index("agents_user_id_fk_idx").on(table.user_id),
  }));

export const agent_api_keys = sqliteTable(
  "agent_api_keys",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    agent_id: text("agent_id")
      .references(() => agents.id, { onDelete: "cascade" })
      .notNull(),
    label: text("label"),
    key_hash: text("key_hash").notNull(),
    key_prefix: text("key_prefix").notNull(),
    revoked_at: ts("revoked_at"),
    last_used_at: ts("last_used_at"),
    createdAt: ts("created_at").$defaultFn(() => new Date()),
  },
  (table) => ({
    keyHashUnique: uniqueIndex("agent_api_keys_key_hash_unique").on(table.key_hash),
    agent_api_keys_agent_id_fk_idx: index("agent_api_keys_agent_id_fk_idx").on(table.agent_id),
  }),
);

export const agent_route_permissions = sqliteTable(
  "agent_route_permissions",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    agent_id: text("agent_id")
      .references(() => agents.id, { onDelete: "cascade" })
      .notNull(),
    scope: text("scope").notNull(),
    route_pattern: text("route_pattern"),
    method: text("method"),
    createdAt: ts("created_at").$defaultFn(() => new Date()),
  },
  (table) => ({
    agentScopeUnique: uniqueIndex("agent_route_permissions_agent_scope_unique").on(
      table.agent_id,
      table.scope,
    ),
  }),
);

export const agent_project_permissions = sqliteTable(
  "agent_project_permissions",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    agent_id: text("agent_id")
      .references(() => agents.id, { onDelete: "cascade" })
      .notNull(),
    project_id: integer("project_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    createdAt: ts("created_at").$defaultFn(() => new Date()),
  },
  (table) => ({
    agentProjectUnique: uniqueIndex("agent_project_permissions_agent_project_unique").on(
      table.agent_id,
      table.project_id,
    ),
    agent_project_permissions_project_id_fk_idx: index("agent_project_permissions_project_id_fk_idx").on(table.project_id),
  }),
);

export const agent_action_logs = sqliteTable("agent_action_logs", {
  id: integer("id", { mode: "number" }).primaryKey({ autoIncrement: true }),
  agent_id: text("agent_id").references(() => agents.id, { onDelete: "set null" }),
  principal_type: text("principal_type").notNull(),
  principal_id: text("principal_id").notNull(),
  user_id: text("user_id")
    .references(() => authSchema.users.id, { onDelete: "cascade" })
    .notNull(),
  request_id: text("request_id").notNull(),
  method: text("method").notNull(),
  route: text("route").notNull(),
  resource: text("resource"),
  project_id: integer("project_id").references(() => projects.id, {
    onDelete: "set null",
  }),
  status_code: integer("status_code"),
  latency_ms: integer("latency_ms"),
  payload_hash: text("payload_hash"),
  error_message: text("error_message"),
  createdAt: ts("created_at").$defaultFn(() => new Date()),
}, (table) => ({
    agent_action_logs_agent_id_fk_idx: index("agent_action_logs_agent_id_fk_idx").on(table.agent_id).where(sql`${table.agent_id} IS NOT NULL`),
    agent_action_logs_user_id_fk_idx: index("agent_action_logs_user_id_fk_idx").on(table.user_id),
    agent_action_logs_project_id_fk_idx: index("agent_action_logs_project_id_fk_idx").on(table.project_id).where(sql`${table.project_id} IS NOT NULL`),
  }));

export const api_idempotency_keys = sqliteTable(
  "api_idempotency_keys",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    user_id: text("user_id")
      .references(() => authSchema.users.id, { onDelete: "cascade" })
      .notNull(),
    project_id: integer("project_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    key_hash: text("key_hash").notNull(),
    request_hash: text("request_hash").notNull(),
    method: text("method").notNull(),
    route: text("route").notNull(),
    status: text("status").notNull().default("in_progress"),
    response_status: integer("response_status"),
    response_headers: text("response_headers", { mode: "json" }).$type<Record<string, string>>(),
    response_body: text("response_body"),
    request_id: text("request_id").notNull(),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    completedAt: ts("completed_at"),
    expiresAt: ts("expires_at").notNull(),
  },
  (table) => ({
    apiIdempotencyKeyUnique: uniqueIndex("api_idempotency_keys_scope_key_unique").on(
      table.user_id,
      table.project_id,
      table.method,
      table.route,
      table.key_hash,
    ),
    api_idempotency_keys_project_id_fk_idx: index("api_idempotency_keys_project_id_fk_idx").on(table.project_id),
  }),
);

export const webhook_endpoints = sqliteTable("webhook_endpoints", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => generateULID()),
  user_id: text("user_id")
    .references(() => authSchema.users.id, { onDelete: "cascade" })
    .notNull(),
  project_id: integer("project_id").references(() => projects.id, {
    onDelete: "set null",
  }),
  url: text("url").notNull(),
  description: text("description"),
  status: text("status").notNull().default("active"),
  events: text("events", { mode: "json" }).$type<string[]>().notNull(),
  signing_secret: text("signing_secret").notNull(),
  signing_secret_hash: text("signing_secret_hash").notNull(),
  createdAt: ts("created_at").$defaultFn(() => new Date()),
  updatedAt: ts("updated_at").$onUpdateFn(() => new Date()),
}, (table) => ({
    webhook_endpoints_user_id_fk_idx: index("webhook_endpoints_user_id_fk_idx").on(table.user_id),
    webhook_endpoints_project_id_fk_idx: index("webhook_endpoints_project_id_fk_idx").on(table.project_id).where(sql`${table.project_id} IS NOT NULL`),
  }));

export const webhook_deliveries = sqliteTable(
  "webhook_deliveries",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    endpoint_id: text("endpoint_id")
      .references(() => webhook_endpoints.id, { onDelete: "cascade" })
      .notNull(),
    event_id: text("event_id").notNull(),
    event_type: text("event_type").notNull(),
    attempt: integer("attempt").notNull().default(1),
    status: text("status").notNull().default("pending"),
    request_body: text("request_body"),
    signature: text("signature"),
    response_status: integer("response_status"),
    response_body: text("response_body"),
    delivered_at: ts("delivered_at"),
    next_attempt_at: ts("next_attempt_at"),
    createdAt: ts("created_at").$defaultFn(() => new Date()),
  },
  (table) => ({
    endpointEventAttemptUnique: uniqueIndex(
      "webhook_deliveries_endpoint_event_attempt_unique",
    ).on(table.endpoint_id, table.event_id, table.attempt),
  }),
);

export const subscriptions = sqliteTable("subscriptions", {
  id: integer("id", { mode: "number" }).primaryKey({ autoIncrement: true }),
  user_id: text("user_id")
    .references(() => authSchema.users.id, { onDelete: "cascade" })
    .notNull(),
  plan: text("plan").notNull().default("basic"),
  status: text("status").notNull().default("active"),
  tokens_included: integer("tokens_included").notNull().default(100),
  tokens_used: integer("tokens_used").notNull().default(0),
  tokens_remaining: integer("tokens_remaining").notNull().default(100),
  tables_limit: integer("tables_limit").notNull().default(999),
  tables_used: integer("tables_used").notNull().default(0),
  billing_cycle_start: ts("billing_cycle_start").notNull(),
  billing_cycle_end: ts("billing_cycle_end").notNull(),
  paddle_subscription_id: text("paddle_subscription_id"),
  paddle_customer_id: text("paddle_customer_id"),
  paddle_product_id: text("paddle_product_id"),
  paddle_price_id: text("paddle_price_id"),
  paddle_transaction_id: text("paddle_transaction_id"),
  next_billing_date: ts("next_billing_date"),
  cancel_at_period_end: integer("cancel_at_period_end", { mode: "boolean" }).default(false),
  storage_limit_bytes: integer("storage_limit_bytes", { mode: "number" })
    .notNull()
    .default(10737418240),
  storage_used_bytes: integer("storage_used_bytes", { mode: "number" })
    .notNull()
    .default(0),
  can_replenish_tokens: integer("can_replenish_tokens", { mode: "boolean" })
    .notNull()
    .default(true),
  collection_mode: text("collection_mode").notNull().default("automatic"),
  createdAt: ts("created_at").$defaultFn(() => new Date()),
  updatedAt: ts("updated_at").$onUpdateFn(() => new Date()),
}, (table) => ({
    subscriptions_user_id_fk_idx: index("subscriptions_user_id_fk_idx").on(table.user_id),
  }));

export const subscription_usage = sqliteTable("subscription_usage", {
  id: integer("id", { mode: "number" }).primaryKey({ autoIncrement: true }),
  subscription_id: integer("subscription_id")
    .references(() => subscriptions.id, { onDelete: "cascade" })
    .notNull(),
  user_id: text("user_id")
    .references(() => authSchema.users.id, { onDelete: "cascade" })
    .notNull(),
  document_id: integer("document_id").references(() => documents.id, {
    onDelete: "cascade",
  }),
  job_id: integer("job_id").references(() => jobs.id, {
    onDelete: "cascade",
  }),
  tokens_consumed: integer("tokens_consumed").notNull().default(1),
  usage_type: text("usage_type").notNull().default("ocr_analysis"),
  createdAt: ts("created_at").$defaultFn(() => new Date()),
}, (table) => ({
  // One charge per document per job: workflow retries and concurrent runs
  // cannot bill the same document twice.
  jobDocumentUsageUnique: uniqueIndex("subscription_usage_job_document_type_unique").on(
    table.job_id,
    table.document_id,
    table.usage_type,
  ),

    subscription_usage_subscription_id_fk_idx: index("subscription_usage_subscription_id_fk_idx").on(table.subscription_id),
    subscription_usage_user_id_fk_idx: index("subscription_usage_user_id_fk_idx").on(table.user_id),
    subscription_usage_document_id_fk_idx: index("subscription_usage_document_id_fk_idx").on(table.document_id).where(sql`${table.document_id} IS NOT NULL`),
  }));

export const free_plan_signups = sqliteTable("free_plan_signups", {
  id: integer("id", { mode: "number" }).primaryKey({ autoIncrement: true }),
  ip_address: text("ip_address").notNull(),
  user_id: text("user_id")
    .references(() => authSchema.users.id, { onDelete: "cascade" })
    .notNull(),
  email_verified_at: ts("email_verified_at"),
  createdAt: ts("created_at").$defaultFn(() => new Date()),
}, (table) => ({
    free_plan_signups_user_id_fk_idx: index("free_plan_signups_user_id_fk_idx").on(table.user_id),
  }));

export const contact_submissions = sqliteTable(
  "contact_submissions",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    name: text("name").notNull(),
    email: text("email").notNull(),
    topic: text("topic").notNull(),
    subject: text("subject").notNull(),
    message: text("message").notNull(),
    status: text("status").notNull().default("new"),
    emailDeliveryStatus: text("email_delivery_status")
      .notNull()
      .default("pending"),
    emailDeliveryError: text("email_delivery_error"),
    emailSentAt: ts("email_sent_at"),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    statusCreatedIdx: index("contact_submissions_status_created_idx").on(
      table.status,
      table.createdAt,
    ),
  }),
);

export const downloads = sqliteTable("downloads", {
  id: integer("id", { mode: "number" }).primaryKey({ autoIncrement: true }),
  user_id: text("user_id")
    .references(() => authSchema.users.id, { onDelete: "cascade" })
    .notNull(),
  project_id: integer("project_id")
    .references(() => projects.id, { onDelete: "cascade" })
    .notNull(),
  table_id: text("table_id").references(() => tables.id, {
    onDelete: "cascade",
  }),
  export_key: text("export_key").notNull(),
  filename: text("filename").notNull(),
  format: text("format").notNull().default("csv"),
  status: text("status").notNull().default("pending"),
  file_size: integer("file_size"),
  record_count: integer("record_count"),
  workflow_instance_id: text("workflow_instance_id"),
  export_type: text("export_type").notNull().default("table"),
  cloudflare_export_id: text("cloudflare_export_id"),
  download_url: text("download_url"),
  expires_at: ts("expires_at"),
  email_sent: integer("email_sent", { mode: "boolean" }).default(false),
  createdAt: ts("created_at").$defaultFn(() => new Date()),
  completedAt: ts("completed_at"),
}, (table) => ({
    downloads_user_id_fk_idx: index("downloads_user_id_fk_idx").on(table.user_id),
    downloads_project_id_fk_idx: index("downloads_project_id_fk_idx").on(table.project_id),
    downloads_table_id_fk_idx: index("downloads_table_id_fk_idx").on(table.table_id).where(sql`${table.table_id} IS NOT NULL`),
  }));

export const project_deletions = sqliteTable("project_deletions", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => generateULID()),
  project_id: integer("project_id").notNull(),
  cloudflare_id: text("cloudflare_id").notNull(),
  user_id: text("user_id").notNull(),
  status: text("status").notNull().default("pending"),
  requested_at: ts("requested_at")
    .$defaultFn(() => new Date())
    .notNull(),
  completed_at: ts("completed_at"),
  failed_at: ts("failed_at"),
  error_message: text("error_message"),
});

export const demo_sessions = sqliteTable("demo_sessions", {
  id: text("id").primaryKey(),
  anonymousUserId: text("anonymous_user_id")
    .references(() => authSchema.users.id, { onDelete: "cascade" })
    .notNull(),
  previewType: text("preview_type"),
  provisionKey: text("provision_key").unique(),
  status: text("status", { enum: ["preparing", "ready", "failed"] }).notNull().default("ready"),
  leaseExpiresAt: ts("lease_expires_at"),
  tokensUsed: integer("tokens_used").notNull().default(0),
  tokensLimit: integer("tokens_limit").notNull().default(15),
  filesUploaded: integer("files_uploaded").notNull().default(0),
  filesLimit: integer("files_limit").notNull().default(5),
  storageUsedBytes: integer("storage_used_bytes").notNull().default(0),
  storageLimitBytes: integer("storage_limit_bytes").notNull().default(52428800),
  maxFileSizeBytes: integer("max_file_size_bytes").notNull().default(5242880),
  maxPagesPerFile: integer("max_pages_per_file").notNull().default(10),
  projectId: integer("project_id").references(() => projects.id, {
    onDelete: "cascade",
  }),
  tableId: text("table_id").references(() => tables.id, {
    onDelete: "set null",
  }),
  createdAt: ts("created_at")
    .$defaultFn(() => new Date())
    .notNull(),
  expiresAt: ts("expires_at").notNull(),
}, (table) => ({
    demo_sessions_anonymous_user_id_fk_idx: index("demo_sessions_anonymous_user_id_fk_idx").on(table.anonymousUserId),
    demo_sessions_project_id_fk_idx: index("demo_sessions_project_id_fk_idx").on(table.projectId).where(sql`${table.projectId} IS NOT NULL`),
    demo_sessions_table_id_fk_idx: index("demo_sessions_table_id_fk_idx").on(table.tableId).where(sql`${table.tableId} IS NOT NULL`),
  }));

export const demo_extraction_reservations = sqliteTable("demo_extraction_reservations", {
  id: text("id").primaryKey(),
  sessionId: text("session_id").notNull().references(() => demo_sessions.id, { onDelete: "cascade" }),
  pages: integer("pages").notNull(),
  createdAt: ts("created_at").notNull().$defaultFn(() => new Date()),
}, (table) => ({
    demo_extraction_reservations_session_id_fk_idx: index("demo_extraction_reservations_session_id_fk_idx").on(table.sessionId),
  }));

export const anonymous_conversion_sessions = sqliteTable(
  "anonymous_conversion_sessions",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    anonymousUserId: text("anonymous_user_id")
      .references(() => authSchema.users.id, { onDelete: "cascade" })
      .notNull(),
    workspaceId: text("workspace_id")
      .references(() => converter_workspaces.id, { onDelete: "cascade" })
      .notNull(),
    tokensUsed: integer("tokens_used").notNull().default(0),
    tokensLimit: integer("tokens_limit").notNull().default(5),
    pagesUploaded: integer("pages_uploaded").notNull().default(0),
    filesUploaded: integer("files_uploaded").notNull().default(0),
    filesLimit: integer("files_limit").notNull().default(3),
    storageUsedBytes: integer("storage_used_bytes").notNull().default(0),
    storageLimitBytes: integer("storage_limit_bytes").notNull().default(15728640),
    maxFileSizeBytes: integer("max_file_size_bytes").notNull().default(5242880),
    maxPagesPerFile: integer("max_pages_per_file").notNull().default(5),
    ipHash: text("ip_hash").notNull(),
    createdAt: ts("created_at").$defaultFn(() => new Date()).notNull(),
    expiresAt: ts("expires_at").notNull(),
  },
  (table) => ({
    anonymousUserUnique: uniqueIndex(
      "anonymous_conversion_sessions_user_unique",
    ).on(table.anonymousUserId),
    ipCreatedIdx: index("anonymous_conversion_sessions_ip_created_idx").on(
      table.ipHash,
      table.createdAt,
    ),
    anonymous_conversion_sessions_workspace_id_fk_idx: index("anonymous_conversion_sessions_workspace_id_fk_idx").on(table.workspaceId),
  }),
);

export const notifications = sqliteTable("notifications", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => generateULID()),
  user_id: text("user_id")
    .references(() => authSchema.users.id, { onDelete: "cascade" })
    .notNull(),
  type: text("type").notNull(),
  title: text("title").notNull(),
  message: text("message").notNull(),
  job_id: integer("job_id").references(() => jobs.id, {
    onDelete: "cascade",
  }),
  project_id: integer("project_id").references(() => projects.id, {
    onDelete: "cascade",
  }),
  table_id: text("table_id").references(() => tables.id, {
    onDelete: "cascade",
  }),
  download_id: integer("download_id").references(() => downloads.id, {
    onDelete: "cascade",
  }),
  link: text("link"),
  dismissed: integer("dismissed", { mode: "boolean" }).default(false),
  read: integer("read", { mode: "boolean" }).default(false),
  createdAt: ts("created_at").$defaultFn(() => new Date()),
  dismissedAt: ts("dismissed_at"),
}, (table) => ({
  ownerDismissedCreatedIdx: index("notifications_owner_dismissed_created_idx").on(table.user_id, table.dismissed, table.createdAt),
  jobOwnerIdx: index("notifications_job_owner_idx").on(table.job_id, table.user_id),

    notifications_project_id_fk_idx: index("notifications_project_id_fk_idx").on(table.project_id).where(sql`${table.project_id} IS NOT NULL`),
    notifications_table_id_fk_idx: index("notifications_table_id_fk_idx").on(table.table_id).where(sql`${table.table_id} IS NOT NULL`),
    notifications_download_id_fk_idx: index("notifications_download_id_fk_idx").on(table.download_id).where(sql`${table.download_id} IS NOT NULL`),
  }));

export const classifierDatasetSplitEnum = ["train", "eval"] as const;
export const classifierLabelSourceEnum = [
  "manual",
  "review",
  "auto_routed",
] as const;
export const classifierEvalModelEnum = ["clef", "clef-flash"] as const;
export const classifierEvalScopeEnum = ["eval", "all"] as const;
export const classifierEvalStatusEnum = [
  "queued",
  "running",
  "succeeded",
  "failed",
] as const;

/** Labeled documents used to evaluate (and later fine-tune) routing models. */
export const classifier_datasets = sqliteTable(
  "classifier_datasets",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    project_id: integer("project_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    user_id: text("user_id")
      .references(() => authSchema.users.id, { onDelete: "cascade" })
      .notNull(),
    name: text("name").notNull(),
    description: text("description"),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: ts("updated_at")
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    projectNameUnique: uniqueIndex("classifier_datasets_project_name_unique").on(
      table.project_id,
      table.name,
    ),
    classifier_datasets_user_id_fk_idx: index("classifier_datasets_user_id_fk_idx").on(table.user_id),
  }),
);

/**
 * One labeled document. `expected_table_id` is the destination a correct
 * router must choose; null means the document matches no routing rule and
 * belongs in review.
 */
export const classifier_dataset_examples = sqliteTable(
  "classifier_dataset_examples",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    dataset_id: text("dataset_id")
      .references(() => classifier_datasets.id, { onDelete: "cascade" })
      .notNull(),
    document_id: integer("document_id")
      .references(() => documents.id, { onDelete: "cascade" })
      .notNull(),
    expected_table_id: text("expected_table_id").references(() => tables.id, {
      onDelete: "cascade",
    }),
    label_source: text("label_source", { enum: classifierLabelSourceEnum })
      .notNull()
      .default("manual"),
    split: text("split", { enum: classifierDatasetSplitEnum })
      .notNull()
      .default("eval"),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: ts("updated_at")
      .$defaultFn(() => new Date())
      .$onUpdateFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    datasetDocumentUnique: uniqueIndex(
      "classifier_dataset_examples_dataset_document_unique",
    ).on(table.dataset_id, table.document_id),
    datasetSplitIdx: index("classifier_dataset_examples_dataset_split_idx").on(
      table.dataset_id,
      table.split,
    ),
    classifier_dataset_examples_document_id_fk_idx: index("classifier_dataset_examples_document_id_fk_idx").on(table.document_id),
    classifier_dataset_examples_expected_table_id_fk_idx: index("classifier_dataset_examples_expected_table_id_fk_idx").on(table.expected_table_id).where(sql`${table.expected_table_id} IS NOT NULL`),
  }),
);

/** One evaluation of the project's routing rules against a dataset. */
export const classifier_eval_runs = sqliteTable(
  "classifier_eval_runs",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    dataset_id: text("dataset_id")
      .references(() => classifier_datasets.id, { onDelete: "cascade" })
      .notNull(),
    project_id: integer("project_id")
      .references(() => projects.id, { onDelete: "cascade" })
      .notNull(),
    user_id: text("user_id")
      .references(() => authSchema.users.id, { onDelete: "cascade" })
      .notNull(),
    model: text("model", { enum: classifierEvalModelEnum }).notNull(),
    split_scope: text("split_scope", { enum: classifierEvalScopeEnum })
      .notNull()
      .default("all"),
    status: text("status", { enum: classifierEvalStatusEnum })
      .notNull()
      .default("queued"),
    routes_snapshot: text("routes_snapshot", { mode: "json" })
      .$type<Array<Record<string, unknown>>>()
      .notNull(),
    metrics: text("metrics", { mode: "json" }).$type<Record<string, unknown>>(),
    example_count: integer("example_count").notNull().default(0),
    completed_count: integer("completed_count").notNull().default(0),
    error_message: text("error_message"),
    workflow_instance_id: text("workflow_instance_id"),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
    startedAt: ts("started_at"),
    finishedAt: ts("finished_at"),
  },
  (table) => ({
    datasetCreatedIdx: index("classifier_eval_runs_dataset_created_idx").on(
      table.dataset_id,
      table.createdAt,
    ),
    classifier_eval_runs_project_id_fk_idx: index("classifier_eval_runs_project_id_fk_idx").on(table.project_id),
    classifier_eval_runs_user_id_fk_idx: index("classifier_eval_runs_user_id_fk_idx").on(table.user_id),
  }),
);

/** Per-document prediction recorded by an evaluation run. */
export const classifier_eval_results = sqliteTable(
  "classifier_eval_results",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => generateULID()),
    eval_run_id: text("eval_run_id")
      .references(() => classifier_eval_runs.id, { onDelete: "cascade" })
      .notNull(),
    document_id: integer("document_id")
      .references(() => documents.id, { onDelete: "cascade" })
      .notNull(),
    expected_table_id: text("expected_table_id"),
    predicted_classifier_id: text("predicted_classifier_id"),
    predicted_table_id: text("predicted_table_id"),
    top_score: integer("top_score"),
    model_confidence: integer("model_confidence"),
    auto_routed: integer("auto_routed", { mode: "boolean" })
      .notNull()
      .default(false),
    correct: integer("correct", { mode: "boolean" }).notNull().default(false),
    scores: text("scores", { mode: "json" }).$type<
      Array<{ classifierId: string; score: number }>
    >(),
    latency_ms: integer("latency_ms"),
    input_tokens: integer("input_tokens"),
    error_message: text("error_message"),
    createdAt: ts("created_at")
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => ({
    runDocumentUnique: uniqueIndex("classifier_eval_results_run_document_unique").on(
      table.eval_run_id,
      table.document_id,
    ),
    classifier_eval_results_document_id_fk_idx: index("classifier_eval_results_document_id_fk_idx").on(table.document_id),
  }),
);

// Re-export all auth schema components
export * from "./auth.schema";
export * from "./oauth.schema";
export const schema = {
  users: authSchema.users,
  sessions: authSchema.sessions,
  accounts: authSchema.accounts,
  verifications: authSchema.verifications,
  authSecondaryStorage: authSchema.authSecondaryStorage,
  jwks: oauthSchema.jwks,
  oauthClient: oauthSchema.oauthClient,
  oauthResource: oauthSchema.oauthResource,
  oauthClientResource: oauthSchema.oauthClientResource,
  oauthRefreshToken: oauthSchema.oauthRefreshToken,
  oauthAccessToken: oauthSchema.oauthAccessToken,
  oauthConsent: oauthSchema.oauthConsent,
  oauthClientAssertion: oauthSchema.oauthClientAssertion,
  userFiles: authSchema.userFiles,
  project_api_keys,
  projects,
  tables,
  project_email_inboxes,
  converter_workspaces,
  conversions,
  conversion_documents,
  dbSchema: db_schema,
  table_metadata,
  column_metadata,
  agents,
  agent_api_keys,
  agent_route_permissions,
  agent_project_permissions,
  agent_action_logs,
  api_idempotency_keys,
  webhook_endpoints,
  webhook_deliveries,
  documents,
  inbound_email_messages,
  inbound_email_attachments,
  email_ingestion_reservations,
  email_ingestion_outbox,
  email_ingestion_rate_buckets,
  document_extraction_attempts,
  splitters,
  splitter_versions,
  splitter_sections,
  processing_config_snapshots,
  processing_runs,
  split_runs,
  document_segments,
  processing_artifacts,
  processing_work_items,
  processing_stage_receipts,
  processing_review_tasks,
  processing_review_decisions,
  processing_outbox,
  jobs,
  batch_processes,
  subscriptions,
  subscription_usage,
  free_plan_signups,
  contact_submissions,
  downloads,
  project_deletions,
  demo_sessions,
  demo_extraction_reservations,
  anonymous_conversion_sessions,
  notifications,
  password_vault,
  classifier_datasets,
  classifier_dataset_examples,
  classifier_eval_runs,
  classifier_eval_results,
};
